import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { api, unwrap, type Schemas } from '../api/client.js';
import { FirstSteps } from '../components/FirstSteps.js';
import { PlusIcon } from '../components/icons.js';
import { PageTitle } from '../components/Layouts.js';
import { HealthTrend, ScoreRing, SeverityBar, UptimeStrip } from '../components/SiteCharts.js';
import {
  Card,
  LinkButton,
  LoadError,
  Loading,
  Notice,
  SelectField,
  StatusBadge,
  TextField,
  type Severity,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDateTime, formatNumber, intlLocale } from '../i18n.js';
import { useMe } from '../session.js';

export const SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low', 'info'];

export function useSites() {
  return useQuery({
    queryKey: ['sites'],
    queryFn: () => unwrap(api.GET('/sites')),
    refetchInterval: 60_000,
  });
}

type Site = Schemas['SiteSummary'];
type Filter = 'all' | 'problems' | 'down';
type Sort = 'priority' | 'name' | 'score' | 'problems';

const urgent = (s: Site) => s.openFindings.critical + s.openFindings.high;
const openTotal = (s: Site) => SEVERITIES.reduce((n, sev) => n + s.openFindings[sev], 0);
const isDown = (s: Site) => s.status !== 'paused' && s.uptimeState === 'down';

/** Down first, then the most urgent problems, then the lowest score; paused at the end. */
const SORTS: Record<Sort, (a: Site, b: Site) => number> = {
  priority: (a, b) =>
    Number(a.status === 'paused') - Number(b.status === 'paused') ||
    Number(isDown(b)) - Number(isDown(a)) ||
    urgent(b) - urgent(a) ||
    (a.healthScore ?? 101) - (b.healthScore ?? 101) ||
    a.name.localeCompare(b.name),
  name: (a, b) => a.name.localeCompare(b.name),
  score: (a, b) => (a.healthScore ?? 101) - (b.healthScore ?? 101),
  problems: (a, b) => openTotal(b) - openTotal(a) || urgent(b) - urgent(a),
};

/** 🇩🇪 from "DE". */
const flagOf = (cc: string) =>
  String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 0x1f1a5 + c.charCodeAt(0)));

function regionName(cc: string, language: string) {
  try {
    return new Intl.DisplayNames([intlLocale(language)], { type: 'region' }).of(cc) ?? cc;
  } catch {
    return cc;
  }
}

/** S2: every site with its score, trends, hosting and open problems at a glance (T-55a). */
export function Overview() {
  const { t } = useTranslation();
  const sites = useSites();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<Sort>('priority');
  const items = sites.data?.items;
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return [...(items ?? [])]
      .filter((s) => !q || s.name.toLowerCase().includes(q) || s.host.toLowerCase().includes(q))
      .filter((s) =>
        filter === 'down' ? isDown(s) : filter === 'problems' ? openTotal(s) > 0 : true,
      )
      .sort(SORTS[sort]);
  }, [items, query, filter, sort]);

  const addLink = (
    <Link
      to="/sites/new"
      className="inline-flex min-h-11 items-center gap-2 rounded-md bg-blue-700 px-4 font-semibold text-white hover:bg-blue-800"
    >
      <PlusIcon />
      {t('overview.add')}
    </Link>
  );

  let content;
  if (sites.isPending) content = <Loading />;
  else if (sites.isError)
    content = (
      <LoadError message={errorMessage(sites.error, t)} onRetry={() => void sites.refetch()} />
    );
  else if (sites.data.items.length === 0)
    content = (
      <Card>
        <h2 className="text-xl font-semibold">{t('overview.empty')}</h2>
        <p className="mt-2 text-slate-800">{t('overview.emptyText')}</p>
      </Card>
    );
  else {
    const down = sites.data.items.filter(isDown).length;
    content = (
      <>
        {down > 0 ? (
          <div className="mb-4">
            <Notice kind="error">{t('banner.openIncidents', { count: down })}</Notice>
          </div>
        ) : null}
        <Summary sites={sites.data.items} />
        <div className="mb-4 grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
          <TextField
            label={t('overview.search')}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <SelectField
            label={t('overview.filter.label')}
            value={filter}
            onChange={(e) => setFilter(e.target.value as Filter)}
          >
            {(['all', 'problems', 'down'] as const).map((f) => (
              <option key={f} value={f}>
                {t(`overview.filter.${f}`)}
              </option>
            ))}
          </SelectField>
          <SelectField
            label={t('overview.sort.label')}
            value={sort}
            onChange={(e) => setSort(e.target.value as Sort)}
          >
            {(['priority', 'name', 'score', 'problems'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`overview.sort.${s}`)}
              </option>
            ))}
          </SelectField>
        </div>
        {shown.length === 0 ? (
          <Notice kind="info">{t('overview.noMatch')}</Notice>
        ) : (
          <ul className="grid gap-4 lg:grid-cols-2">
            {shown.map((site) => (
              <li key={site.id}>
                <SiteCard site={site} />
              </li>
            ))}
          </ul>
        )}
      </>
    );
  }
  return (
    <>
      <PageTitle actions={addLink}>{t('overview.title')}</PageTitle>
      <FirstSteps />
      {content}
    </>
  );
}

/** Four figures over all sites. */
function Summary({ sites }: { sites: readonly Site[] }) {
  const { t, i18n } = useTranslation();
  const active = sites.filter((s) => s.status !== 'paused');
  const online = active.filter((s) => s.uptimeState === 'up').length;
  const scored = sites.filter((s) => s.healthScore !== null);
  const avgScore = scored.length
    ? Math.round(scored.reduce((n, s) => n + (s.healthScore ?? 0), 0) / scored.length)
    : null;
  const uptimes = sites.map((s) => s.uptime30d).filter((u): u is number => u !== null);
  const avgUptime = uptimes.length ? uptimes.reduce((a, b) => a + b, 0) / uptimes.length : null;
  const urgentCount = sites.reduce((n, s) => n + urgent(s), 0);
  const tiles: [string, string, string][] = [
    [
      t('overview.summary.sites'),
      String(sites.length),
      t('overview.summary.online', { count: online, total: active.length }),
    ],
    [
      t('overview.summary.score'),
      avgScore === null ? '–' : `${avgScore}`,
      t('overview.summary.scoreHint'),
    ],
    [t('overview.summary.urgent'), String(urgentCount), t('overview.summary.urgentHint')],
    [
      t('overview.summary.uptime'),
      avgUptime === null ? '–' : `${formatNumber(avgUptime, i18n.language, 2)} %`,
      t('overview.summary.uptimeHint'),
    ],
  ];
  return (
    <section aria-label={t('overview.summary.label')} className="mb-6">
      <dl className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {tiles.map(([label, value, hint]) => (
          <div key={label} className="rounded-lg border border-slate-200 bg-white p-4">
            <dt className="text-sm text-slate-700">{label}</dt>
            <dd className="mt-1 text-2xl font-bold tabular-nums">{value}</dd>
            <dd className="text-sm text-slate-700">{hint}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function SiteCard({ site }: { site: Site }) {
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const trend = site.healthTrend;
  const delta =
    trend.length >= 2 ? (trend[trend.length - 1]?.score ?? 0) - (trend[0]?.score ?? 0) : null;
  const h = site.hosting;
  const days = (n: number | null) =>
    n === null ? t('overview.noData') : t('overview.card.daysLeft', { count: n });
  const facts: [string, ReactNode, (string | undefined)?][] = [
    [
      t('overview.card.location'),
      h?.country ? (
        <span title={`IP ${h.ip}`}>
          <span aria-hidden="true">{flagOf(h.country)} </span>
          {regionName(h.country, i18n.language)}
          {h.cdn ? ` · ${t('overview.card.viaCdn', { cdn: h.cdn })}` : null}
        </span>
      ) : (
        t('overview.noData')
      ),
    ],
    [
      t('overview.card.hosting'),
      h?.org ? (
        <span title={h.asn ? `AS${h.asn}` : undefined}>
          {h.org}
          {h.asn ? <span className="text-slate-600"> · AS{h.asn}</span> : null}
        </span>
      ) : (
        t('overview.noData')
      ),
    ],
    [t('overview.card.cms'), site.cmsDetected ?? t('overview.card.cmsUnknown')],
    [
      t('overview.card.response'),
      site.responseMs === null ? t('overview.noData') : `${site.responseMs} ms`,
      site.responseMs !== null && site.responseMs > 1500 ? 'text-red-800' : undefined,
    ],
    [
      t('overview.card.ssl'),
      days(site.sslDaysLeft),
      site.sslDaysLeft !== null && site.sslDaysLeft < 14 ? 'text-red-800' : undefined,
    ],
    [
      t('overview.card.domain'),
      days(site.domainDaysLeft),
      site.domainDaysLeft !== null && site.domainDaysLeft < 30 ? 'text-red-800' : undefined,
    ],
  ];
  return (
    <Card className="flex h-full flex-col gap-4">
      <div className="flex items-start gap-4">
        <ScoreRing score={site.healthScore} grade={site.healthGrade} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="min-w-0 text-lg font-semibold">
              <Link
                to={`/sites/${site.id}`}
                aria-label={t('overview.open', { name: site.name })}
                className="break-words hover:underline focus-visible:outline-2 focus-visible:outline-blue-700"
              >
                {site.name}
              </Link>
            </h2>
            <StatusBadge state={site.status === 'paused' ? 'paused' : site.uptimeState} />
          </div>
          <p className="break-all text-sm text-slate-700">{site.url}</p>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            {delta !== null ? (
              <span
                className={`font-medium ${delta > 0 ? 'text-green-800' : delta < 0 ? 'text-red-800' : 'text-slate-700'}`}
              >
                {delta === 0
                  ? t('overview.card.deltaSame')
                  : t(delta > 0 ? 'overview.card.deltaUp' : 'overview.card.deltaDown', {
                      points: Math.abs(delta),
                    })}
              </span>
            ) : null}
            <span className="text-slate-700">
              {t('overview.uptime30d')}:{' '}
              <strong className="tabular-nums">
                {site.uptime30d === null
                  ? t('overview.noData')
                  : `${formatNumber(site.uptime30d, i18n.language, 2)} %`}
              </strong>
            </span>
          </p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <HealthTrend points={trend} />
        <UptimeStrip days={site.uptimeDays} today={new Date()} />
      </div>

      <div>
        <h3 className="mb-1 text-sm font-medium text-slate-800">{t('overview.problems')}</h3>
        <SeverityBar siteId={site.id} counts={site.openFindings} order={SEVERITIES} />
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
        {facts.map(([label, value, tone]) => (
          <div key={label} className="min-w-0">
            <dt className="text-slate-700">{label}</dt>
            <dd className={`break-words font-medium ${tone ?? 'text-slate-900'}`}>{value}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
        <span className="text-sm text-slate-700">
          {site.lastCheckAt
            ? t('overview.card.lastCheck', {
                time: formatDateTime(site.lastCheckAt, i18n.language, timeZone),
              })
            : t('site.notChecked')}
        </span>
        <span className="flex flex-wrap gap-2">
          <LinkButton to={`/sites/${site.id}/findings`} variant="secondary" size="sm">
            {t('overview.card.findings')}
          </LinkButton>
          <LinkButton to={`/sites/${site.id}`} variant="primary" size="sm">
            {t('overview.card.details')}
          </LinkButton>
        </span>
      </div>
    </Card>
  );
}
